import Dexie, { type Table } from 'dexie';
import type { ImportResult } from '../importer/types';
import type { Group, Job, ProgressEvent, Subject, User } from './schema';

export interface StagedImportRow {
  jobId: string;
  result: ImportResult;
  createdAt: string;
}

/**
 * IndexedDB-backed local store (via Dexie). This is the app's real source
 * of truth for offline use — once a job is imported, every screen reads
 * from here, never from the network. See docs/ADR.md for why IndexedDB
 * (not localStorage) and how sync/offline conflict handling works.
 */
export class QrCardsDatabase extends Dexie {
  users!: Table<User, string>;
  jobs!: Table<Job, string>;
  groups!: Table<Group, string>;
  subjects!: Table<Subject, string>;
  progressEvents!: Table<ProgressEvent, string>;
  /** Parsed-but-not-yet-confirmed import results, keyed by draft jobId, so
   * the Import Review screen survives a refresh mid-review. Cleared once
   * confirmImport() commits (or the draft is discarded). */
  stagedImports!: Table<StagedImportRow, string>;

  constructor() {
    super('photollama-qr-cards');
    this.version(1).stores({
      users: 'id',
      jobs: 'id, importStatus, shootDate',
      groups: 'id, jobId, [jobId+order]',
      // Compound/multi indexes give instant lookup by normalised name,
      // group, short code, and internal reference (spec requirement).
      subjects: 'id, jobId, groupId, [jobId+groupId], searchNormalized, shortCode, internalReference, status',
      progressEvents: 'id, jobId, subjectId, syncState',
      stagedImports: 'jobId'
    });
  }
}

export const db = new QrCardsDatabase();

/** Stable per-browser-install device id, used to attribute ProgressEvents
 * and for remote sign-out/revocation bookkeeping. Not a secret. */
export function getDeviceId(): string {
  const key = 'qr-cards-device-id';
  let id = localStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(key, id);
  }
  return id;
}
